import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Download, Trash2, Calendar } from 'lucide-react';
import { useUserGuides, useDeleteUserGuide, UserGuide } from "@/hooks/use-user-guides";
import { useSimpleRole } from "@/hooks/use-simple-role";

export const UserGuidesList: React.FC = () => {
  const { data: guides, isLoading } = useUserGuides();
  const { isSystemAdmin } = useSimpleRole();
  const deleteMutation = useDeleteUserGuide();
  const [guideToDelete, setGuideToDelete] = useState<UserGuide | null>(null);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleDownload = async (guide: UserGuide) => {
    try {
      const response = await fetch(guide.file_url);
      const blob = await response.blob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = guide.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      // Fallback to direct link
      const link = document.createElement('a');
      link.href = guide.file_url;
      link.download = guide.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDelete = (guide: UserGuide) => {
    setGuideToDelete(guide);
  };

  const confirmDelete = () => {
    if (guideToDelete) {
      deleteMutation.mutate(guideToDelete);
      setGuideToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-3 bg-muted rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!guides || guides.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No user guides available</h3>
            <p className="text-muted-foreground">
              {isSystemAdmin 
                ? "Upload your first user guide to get started."
                : "Check back later for user guides and documentation."
              }
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {guides.map((guide) => (
          <Card key={guide.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1 flex-1">
                  <CardTitle className="text-lg text-left">{guide.title}</CardTitle>
                  {guide.description && (
                    <CardDescription className="text-left">{guide.description}</CardDescription>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(guide)}
                    className="flex items-center space-x-1 text-black hover:opacity-80"
                    style={{ backgroundColor: "#DFEF97" }}
                  >
                    <Download className="h-3 w-3" />
                    <span>Download</span>
                  </Button>
                  {isSystemAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(guide)}
                      className="flex items-center space-x-1 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <FileText className="h-3 w-3" />
                    <span>{guide.file_name}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {formatFileSize(guide.file_size)}
                  </Badge>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(guide.created_at)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!guideToDelete} onOpenChange={() => setGuideToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Guide</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{guideToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};